import React from 'react';
import { motion } from 'framer-motion';
import type { Entry } from '../types/raffle';
import { arcPath, fillFor, labelSize, truncate } from '../utils/wheel';
interface WheelProps {
  pool: Entry[];
  rotation: number;
  spinning: boolean;
  spinSeconds: number;
  canSpin: boolean;
  onSpin: () => void;
  onSpinComplete: () => void;
}
const SIZE = 400;
const C = SIZE / 2;
const R = 188;
export function Wheel({
  pool,
  rotation,
  spinning,
  spinSeconds,
  canSpin,
  onSpin,
  onSpinComplete
}: WheelProps) {
  const count = pool.length;
  const seg = count > 0 ? 360 / count : 360;
  const fontSize = labelSize(count);
  return <div className="relative mx-auto aspect-square w-full max-w-[36rem]" data-id="element-316" data-mp="1xbgc0a1z4gt7">
      {/* Pointer */}
      <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2 -translate-y-[2px]" data-id="element-317" data-mp="21ci5095arwmq">
        <svg width="30" height="34" viewBox="0 0 30 34" aria-hidden="true" data-id="element-318" data-mp="3rp7mfbs4n4pp">
          <path d="M15 33 L2 4 A 14 14 0 0 1 28 4 Z" fill="#E3B23C" data-id="element-319" data-mp="3vq9mfutvbksq" />
          <path d="M15 28 L7 8 A 9 9 0 0 1 23 8 Z" fill="#0A0E13" opacity="0.28" data-id="element-320" data-mp="3vnf01xranpwo" />
        </svg>
      </div>

      <motion.div className="h-full w-full" animate={{
      rotate: rotation
    }} transition={{
      duration: spinSeconds,
      ease: [0.14, 0.86, 0.08, 1]
    }} onAnimationComplete={onSpinComplete} data-id="element-321" data-mp="3s1xlm51ol385">
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="h-full w-full" role="img" aria-label={count > 0 ? `Prize wheel with ${count} names: ${pool.map(e => e.name).join(', ')}` : 'Empty prize wheel'} data-id="element-322" data-mp="5ltdgky7u94v">
          <circle cx={C} cy={C} r={196} fill="#18212B" data-id="element-323" data-mp="3o0m46msynob1" />
          <circle cx={C} cy={C} r={196} fill="none" stroke="#E3B23C" strokeWidth="3" data-id="element-324" data-mp="3ny9etokvibny" />
          {count === 0 && <circle cx={C} cy={C} r={R} fill="#15202B" data-id="element-325" data-mp="3j3lxqas78jje" />}
          {count === 1 && <circle cx={C} cy={C} r={R} fill={fillFor(0, 1)} data-id="element-326" data-mp="138jz79ccilu7" />}
          {count > 1 && pool.map((entry, i) => <path key={entry.id} d={arcPath(C, C, R, i * seg, (i + 1) * seg)} fill={fillFor(i, count)} stroke="#0A0E13" strokeWidth="1" data-id="element-327" data-mp="ydcz0biabza1" data-mpi={i} />)}
          {count > 0 && pool.map((entry, i) => {
          const center = count === 1 ? 90 : i * seg + seg / 2;
          return <g key={`${entry.id}-label`} transform={`rotate(${center - 90} ${C} ${C})`} data-id="element-328" data-mp="1k94x9vhltmtt" data-mpi={i}>
                  <text x={C + R - 16} y={C} textAnchor="end" dominantBaseline="central" fill="#F2F6FA" fontSize={fontSize} fontWeight={600} style={{
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '0.01em'
            }} data-id="element-329" data-mp="mlw4rgj6rdib">
                    {truncate(entry.name, count)}
                  </text>
                </g>;
        })}
          <circle cx={C} cy={C} r={54} fill="#0A0E13" stroke="#25313E" strokeWidth="2" data-id="element-330" data-mp="2m6lpzwbx9nfw" />
        </svg>
      </motion.div>

      <button type="button" onClick={onSpin} disabled={!canSpin || spinning} className="absolute left-1/2 top-1/2 z-20 h-[23%] w-[23%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold text-[clamp(0.7rem,1.9vw,0.95rem)] font-bold uppercase tracking-[0.14em] text-ink transition-[transform,background-color] duration-150 ease-emphasis hover:bg-[#F0C154] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold active:scale-95 disabled:cursor-not-allowed disabled:bg-line disabled:text-muted disabled:active:scale-100" data-id="element-331" data-mp="2i4h1td48tj0n">
        {spinning ? '…' : 'Spin'}
      </button>
    </div>;
}